import React, { useState, useContext } from "react";
import { motion } from "framer-motion";
import {
  CreditCard,
  Building2,
  X,
  AlertCircle,
  Loader2,
  ChevronDown,
  Wallet,
} from "lucide-react";
import API from "../../utils/api";
import useModal from "../../hooks/use-modal";
import { MainStore } from "../../stores/Main";
import SecureInput from "../../components/secure-input/SecureInput";
import SecureCreditCardInput from "../../components/secure-input/SecureCreditCardInput";
import Select from "react-select";
import { trackPaymentMethodAdded } from "../../utils/posthogTracking";

const ErrorMessage = ({ children }) => (
  <motion.div
    initial={{ opacity: 0, y: -10 }}
    animate={{ opacity: 1, y: 0 }}
    className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-2"
    role="alert"
  >
    <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
    <span className="text-sm text-red-300">{children}</span>
  </motion.div>
);

const states = [
  { label: "Alabama", value: "AL" },
  { label: "Alaska", value: "AK" },
  { label: "Arizona", value: "AZ" },
  { label: "Arkansas", value: "AR" },
  { label: "California", value: "CA" },
  { label: "Colorado", value: "CO" },
  { label: "Connecticut", value: "CT" },
  { label: "Delaware", value: "DE" },
  { label: "District Of Columbia", value: "DC" },
  { label: "Florida", value: "FL" },
  { label: "Georgia", value: "GA" },
  { label: "Hawaii", value: "HI" },
  { label: "Idaho", value: "ID" },
  { label: "Illinois", value: "IL" },
  { label: "Indiana", value: "IN" },
  { label: "Iowa", value: "IA" },
  { label: "Kansas", value: "KS" },
  { label: "Kentucky", value: "KY" },
  { label: "Louisiana", value: "LA" },
  { label: "Maine", value: "ME" },
  { label: "Maryland", value: "MD" },
  { label: "Massachusetts", value: "MA" },
  { label: "Michigan", value: "MI" },
  { label: "Minnesota", value: "MN" },
  { label: "Mississippi", value: "MS" },
  { label: "Missouri", value: "MO" },
  { label: "Montana", value: "MT" },
  { label: "Nebraska", value: "NE" },
  { label: "Nevada", value: "NV" },
  { label: "New Hampshire", value: "NH" },
  { label: "New Jersey", value: "NJ" },
  { label: "New Mexico", value: "NM" },
  { label: "New York", value: "NY" },
  { label: "North Carolina", value: "NC" },
  { label: "North Dakota", value: "ND" },
  { label: "Ohio", value: "OH" },
  { label: "Oklahoma", value: "OK" },
  { label: "Oregon", value: "OR" },
  { label: "Pennsylvania", value: "PA" },
  { label: "Rhode Island", value: "RI" },
  { label: "South Carolina", value: "SC" },
  { label: "South Dakota", value: "SD" },
  { label: "Tennessee", value: "TN" },
  { label: "Texas", value: "TX" },
  { label: "Utah", value: "UT" },
  { label: "Vermont", value: "VT" },
  { label: "Virginia", value: "VA" },
  { label: "Washington", value: "WA" },
  { label: "West Virginia", value: "WV" },
  { label: "Wisconsin", value: "WI" },
  { label: "Wyoming", value: "WY" },
];

const customSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: "rgba(17, 24, 39, 0.5)",
    borderColor: state.isFocused
      ? "rgba(59, 130, 246, 0.5)"
      : "rgba(75, 85, 99, 0.5)",
    boxShadow: state.isFocused ? "0 0 0 3px rgba(59, 130, 246, 0.1)" : "none",
    "&:hover": {
      borderColor: "rgba(59, 130, 246, 0.5)",
    },
    minHeight: "48px",
    borderRadius: "0.75rem",
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: "rgba(17, 24, 39, 0.95)",
    backdropFilter: "blur(12px)",
    borderRadius: "0.75rem",
    border: "1px solid rgba(75, 85, 99, 0.5)",
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? "rgba(59, 130, 246, 0.3)"
      : state.isFocused
        ? "rgba(59, 130, 246, 0.1)"
        : "transparent",
    color: state.isSelected ? "#fff" : "rgba(209, 213, 219, 1)",
    "&:hover": {
      backgroundColor: "rgba(59, 130, 246, 0.2)",
      color: "#fff",
    },
  }),
  singleValue: (provided) => ({
    ...provided,
    color: "#fff",
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "rgba(156, 163, 175, 1)",
  }),
  input: (provided) => ({
    ...provided,
    color: "#fff",
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    color: "rgba(156, 163, 175, 1)",
    "&:hover": {
      color: "#fff",
    },
  }),
  indicatorSeparator: (provided) => ({
    ...provided,
    backgroundColor: "rgba(75, 85, 99, 0.5)",
  }),
};

const ModalAddMethod = (props) => {
  const { method, afterAdd, back, backData } = props.modalProps;
  const { closeModal, openModal } = useModal();
  const mainStore = useContext(MainStore);
  const rawPemKey = mainStore.agencyDetails?.pemkey;

  // Decode pemKey if it's base64 encoded
  const pemKey = rawPemKey
    ? typeof rawPemKey === "object" && rawPemKey.key
      ? atob(rawPemKey.key) // If it's an object with a key property, decode it
      : rawPemKey.includes("BEGIN")
        ? rawPemKey // If it already contains BEGIN, it's not encoded
        : atob(rawPemKey) // Otherwise try to decode it
    : null;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [paymentType, setPaymentType] = useState(method || "credit-card");

  // Form fields
  const [formData, setFormData] = useState({
    // Common fields
    cardAccountHolder: "",
    address: "",
    city: "",
    state: "",
    zip: "",

    // Credit card fields
    cardNumber: "",
    cardType: "",
    expiryMonth: "",
    expiryYear: "",
    cvv: "",

    // Bank account fields
    accountHolder: "",
    accountType: "checking",
    accountNumber: "",
    routingNumber: "",

    // Other
  });

  const [encryptedData, setEncryptedData] = useState({
    cardNumber: null,
    cvv: null,
    accountNumber: null,
  });

  const contentAnimation = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        staggerChildren: 0.1,
      },
    },
  };

  const itemAnimation = {
    hidden: { opacity: 0, x: -10 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.2 },
    },
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleCreditCardChange = (data) => {
    setEncryptedData((prev) => ({
      ...prev,
      cardNumber: data.encryptedValue,
    }));
    setFormData((prev) => ({
      ...prev,
      cardType: data.cardType,
    }));
  };

  const handleCVVChange = (data) => {
    setEncryptedData((prev) => ({
      ...prev,
      cvv: data.encryptedValue,
    }));
  };

  const handleAccountNumberChange = (data) => {
    setEncryptedData((prev) => ({
      ...prev,
      accountNumber: data.encryptedValue,
    }));
  };

  const validateForm = () => {
    if (paymentType === "credit-card") {
      if (!formData.cardAccountHolder) return false;
      if (!formData.zip || !formData.state) return false;
      if (!encryptedData.cardNumber || !encryptedData.cvv) return false;
      if (!formData.expiryMonth || !formData.expiryYear) return false;
      if (!formData.cardType) return false;

      // Validate expiry date
      const currentDate = new Date();
      const currentYear = currentDate.getFullYear();
      const currentMonth = currentDate.getMonth() + 1;
      const expiryYear = parseInt(formData.expiryYear, 10);
      const expiryMonth = parseInt(formData.expiryMonth, 10);

      if (
        expiryYear < currentYear ||
        (expiryYear === currentYear && expiryMonth < currentMonth)
      ) {
        return false;
      }
    } else if (paymentType === "bank-account") {
      if (!formData.accountHolder) return false;
      if (!formData.zip || !formData.state) return false;
      if (!formData.routingNumber || formData.routingNumber.length !== 9)
        return false;
      if (!encryptedData.accountNumber) return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      setError("Please fill in all required fields");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let paymentData;

      if (paymentType === "credit-card") {
        paymentData = {
          card: {
            accountHolder: formData.cardAccountHolder,
            cardType: formData.cardType,
            cardNumber: encryptedData.cardNumber,
            expiry: `${formData.expiryMonth.padStart(2, "0")}/${formData.expiryYear}`,
            billingAddress: formData.address || "",
            billingAddress2: "",
            billingCity: formData.city || "",
            billingState: formData.state || "",
            billingZip: formData.zip || "",
            billingCountry: "US",
            cvv: encryptedData.cvv,
          },
          saveForLater: true,
        };
      } else {
        paymentData = {
          bank: {
            accountHolder: formData.accountHolder,
            accountType: formData.accountType,
            accountNumber: encryptedData.accountNumber,
            routingNumber: formData.routingNumber,
            billingAddress: formData.address || "",
            billingAddress2: "",
            billingCity: formData.city || "",
            billingState: formData.state || "",
            billingZip: formData.zip || "",
            billingCountry: "US",
          },
          saveForLater: true,
        };
      }

      const response = await API.post("/payment-methods", paymentData);

      if (response.data.result_code) {
        // Track failed payment method addition
        trackPaymentMethodAdded(
          paymentType === "credit-card" ? "credit_card" : "bank_account",
          false
        );
        setError("Unable to save payment method. Please try again.");
      } else {
        // Track successful payment method addition
        trackPaymentMethodAdded(
          paymentType === "credit-card" ? "credit_card" : "bank_account",
          true
        );
        const createdId = response.data.id;

        // Attempt to fetch fresh methods and select the created one
        let selectedMethod = null;
        try {
          const methodsResp = await API.get("/payment-methods");
          const list = methodsResp?.data?.payment_methods || [];
          const found = list.find((m) => m.id === createdId);
          if (found) {
            selectedMethod = {
              value: found.id,
              label: found.method_desc,
              def: found.default,
              new: false,
              method_type: found.method_type,
              billing_zip: found.billing_zip,
              billing_state: found.billing_state,
            };
          }
        } catch (_) {}

        // Fallback to local construction if not found
        if (!selectedMethod) {
          const methodType =
            paymentType === "credit-card"
              ? (formData.cardType || "").toUpperCase()
              : (formData.accountType || "").toUpperCase();
          selectedMethod = {
            value: createdId,
            label:
              paymentType === "credit-card"
                ? `${formData.cardType} ending in ****`
                : `${formData.accountType} ending in ****`,
            new: false,
            method_type: methodType,
            billing_zip: formData.zip || "",
            billing_state: formData.state || "",
          };
        }

        if (!back) {
          afterAdd && afterAdd(selectedMethod);
          closeModal();
        } else {
          // Spread all backData properties and update the form with the new method
          const newData = {
            ...backData,
            form: { ...backData.form, method: selectedMethod },
          };
          openModal(back, newData);
        }
      }
    } catch (err) {
      console.error("Error saving payment method:", err);
      setError("An error occurred while saving the payment method.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-full max-h-[100vh] sm:max-h-[90vh] p-0">
      {/* Modal Header */}
      <div className="relative bg-gradient-to-r from-blue-600 to-blue-700 px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8 flex-shrink-0 sm:rounded-t-2xl">
        <button
          onClick={closeModal}
          className="absolute top-3 right-3 sm:top-4 sm:right-4 p-1.5 sm:p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors group z-10"
        >
          <X className="w-4 h-4 sm:w-5 sm:h-5 text-white group-hover:rotate-90 transition-transform duration-300" />
        </button>

        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="flex items-center gap-3 sm:gap-4 pr-10"
        >
          <div className="w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center flex-shrink-0">
            <Wallet className="w-5 h-5 sm:w-6 sm:h-6 lg:w-7 lg:h-7 text-white" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-white truncate">
              Add Payment Method
            </h2>
            <p className="text-blue-100 text-sm sm:text-base">
              Securely add a new payment method
            </p>
          </div>
        </motion.div>
      </div>

      {/* Modal Body */}
      <motion.div
        className="p-4 sm:p-6 lg:p-8 overflow-y-auto flex-1 min-h-0 overscroll-contain bg-gray-900"
        variants={contentAnimation}
        initial="hidden"
        animate="visible"
      >
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Payment Type Selector */}
          <div className="flex gap-2 p-1 bg-gray-800 rounded-lg">
            <button
              type="button"
              onClick={() => setPaymentType("credit-card")}
              className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-md transition-all ${
                paymentType === "credit-card"
                  ? "bg-blue-600 text-white"
                  : "text-gray-400 hover:text-gray-200 hover:bg-gray-700"
              }`}
            >
              <CreditCard className="w-4 h-4" />
              <span>Credit Card</span>
            </button>
            <button
              type="button"
              onClick={() => setPaymentType("bank-account")}
              className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-md transition-all ${
                paymentType === "bank-account"
                  ? "bg-blue-600 text-white"
                  : "text-gray-400 hover:text-gray-200 hover:bg-gray-700"
              }`}
            >
              <Building2 className="w-4 h-4" />
              <span>Bank Account</span>
            </button>
          </div>

          {/* Credit Card Form */}
          {paymentType === "credit-card" && (
            <motion.div variants={itemAnimation} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Account Holder Name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  name="cardAccountHolder"
                  value={formData.cardAccountHolder}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                  placeholder="John Doe"
                />
              </div>

              <SecureCreditCardInput
                pemKey={pemKey}
                onChange={handleCreditCardChange}
                required
                methods={["visa", "mastercard", "amex", "discover"]}
              />

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Expiry Month <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    name="expiryMonth"
                    value={formData.expiryMonth}
                    onChange={(e) => {
                      let value = e.target.value.replace(/\D/g, "");
                      if (value.length === 1) {
                        if (!["0", "1"].includes(value)) return;
                      }
                      if (value.length === 2) {
                        const num = parseInt(value, 10);
                        if (num < 1 || num > 12) return;
                      }
                      if (value.length <= 2) {
                        setFormData((prev) => ({
                          ...prev,
                          expiryMonth: value,
                        }));
                      }
                    }}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                    placeholder="MM"
                    maxLength="2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Expiry Year <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    name="expiryYear"
                    value={formData.expiryYear}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, "");
                      if (value.length <= 4) {
                        setFormData((prev) => ({ ...prev, expiryYear: value }));
                      }
                    }}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                    placeholder="YYYY"
                    maxLength="4"
                  />
                </div>
                <div>
                  <SecureInput
                    pemKey={pemKey}
                    onChange={handleCVVChange}
                    label="CVV"
                    placeholder="123"
                    required
                    maxLength={4}
                    pattern={/[^\d]/g}
                    validator={(value) => {
                      const numericValue = value.replace(/\D/g, "");
                      return (
                        numericValue.length >= 3 && numericValue.length <= 4
                      );
                    }}
                  />
                </div>
              </div>
            </motion.div>
          )}

          {/* Bank Account Form */}
          {paymentType === "bank-account" && (
            <motion.div variants={itemAnimation} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Account Holder Name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  name="accountHolder"
                  value={formData.accountHolder}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                  placeholder="John Doe"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Account Type <span className="text-red-400">*</span>
                  </label>
                  <select
                    name="accountType"
                    value={formData.accountType}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                  >
                    <option value="checking">Checking</option>
                    <option value="savings">Savings</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Routing Number <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    name="routingNumber"
                    value={formData.routingNumber}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, "");
                      if (value.length <= 9) {
                        setFormData((prev) => ({
                          ...prev,
                          routingNumber: value,
                        }));
                      }
                    }}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                    placeholder="123456789"
                    maxLength="9"
                  />
                </div>
              </div>

              <SecureInput
                pemKey={pemKey}
                onChange={handleAccountNumberChange}
                label="Account Number"
                placeholder="Account number"
                required
                validator={(value) => {
                  const numericValue = value.replace(/\D/g, "");
                  return numericValue.length >= 4 && numericValue === value;
                }}
              />
            </motion.div>
          )}

          {/* Billing Address (Common Fields) */}
          <motion.div variants={itemAnimation} className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-200">
              Billing Address
            </h3>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Street Address
              </label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                placeholder="123 Main St"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  City
                </label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                  placeholder="New York"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  ZIP Code <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  name="zip"
                  value={formData.zip}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, "");
                    if (value.length <= 5) {
                      setFormData((prev) => ({ ...prev, zip: value }));
                    }
                  }}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                  placeholder="10001"
                  maxLength="5"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                State <span className="text-red-400">*</span>
              </label>
              <Select
                options={states}
                styles={customSelectStyles}
                placeholder="Select state"
                value={
                  states.find(({ value }) => value === formData.state) || null
                }
                onChange={(selected) =>
                  setFormData((prev) => ({
                    ...prev,
                    state: selected?.value || "",
                  }))
                }
                classNamePrefix="react-select"
              />
            </div>
          </motion.div>

          {/* Error Message */}
          {error && <ErrorMessage>{error}</ErrorMessage>}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="submit"
              disabled={loading || !validateForm()}
              className={`flex-1 py-3 px-6 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${
                loading || !validateForm()
                  ? "bg-gray-700 text-gray-400 cursor-not-allowed"
                  : "bg-blue-600 text-white hover:bg-blue-700"
              }`}
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              {loading ? "Saving..." : "Save Payment Method"}
            </button>

            {back && (
              <button
                type="button"
                onClick={() => openModal(back, backData)}
                disabled={loading}
                className="py-3 px-6 bg-gray-800 text-gray-300 rounded-lg font-medium hover:bg-gray-700 transition-colors"
              >
                Go Back
              </button>
            )}
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default ModalAddMethod;
